import { observer } from 'mobx-react-lite'
import { useId, useState } from 'react'
import { IoChevronDown } from 'react-icons/io5'

import { userManager } from '@/Core/Managers/User'
import { FormField } from '@/Core/UI/Form'
import { FormRow, FormSection } from '@/Ship/Components/Form/FormSection'
import { cx } from '@/Ship/Helpers/cssClass'

import styles from './RequestFormTemplate.module.css'

interface RequestDetailsSectionProps {
    formContext: any
}

/**
 * «Дані заявки» — згортувана секція полів заявки. У перегляді згорнута (ключове вже у
 * зведенні), у редагуванні/створенні розгорнута. ERP-поля править лише адмін (дзеркало
 * бекендної групи `transport_request:admin:update`); диспетчер у редагуванні — лише коментар.
 */
export const RequestDetailsSection = observer(({ formContext }: RequestDetailsSectionProps) => {
    const isView = formContext?.isEditable === false
    const [open, setOpen] = useState(!isView)
    const panelId = useId()
    const v: any = formContext?.values ?? {}

    const roles = (userManager.user?.['roles'] as string[] | undefined) ?? []
    const erpFieldEditable = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN')

    const hint = [v.vehicleType?.title, v.organizationUnit?.title].filter(Boolean).join(' · ')

    return (
        <section className={cx(styles, 'details')} aria-label="Дані заявки">
            <button
                type="button"
                className={cx(styles, 'detailsToggle')}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen((value) => !value)}
            >
                <span className={cx(styles, 'detailsTitle')}>Дані заявки</span>
                {hint && !open && <span className={cx(styles, 'detailsHint')}>{hint}</span>}
                <IoChevronDown
                    className={`${cx(styles, 'chevron')} ${open ? cx(styles, 'chevronOpen') : ''}`}
                    aria-hidden="true"
                />
            </button>
            {/* Панель завжди в DOM: розгортання анімується через grid-template-rows */}
            <div id={panelId} className={`${cx(styles, 'detailsPanel')} ${open ? cx(styles, 'detailsPanelOpen') : ''}`}>
                <div className={cx(styles, 'detailsBody')} aria-hidden={!open}>
                    <div className={cx(styles, 'detailsInner')}>
                        <FormSection>
                            <FormRow>
                                <FormField name="productionObject" isEditable={erpFieldEditable} />
                                <FormField name="constructionObject" isEditable={erpFieldEditable} />
                            </FormRow>
                            <FormRow>
                                <FormField name="vehicleType" isEditable={erpFieldEditable} />
                                <FormField name="organizationUnit" isEditable={erpFieldEditable} />
                            </FormRow>
                            <FormRow>
                                <FormField name="responsibleCustomer" isEditable={erpFieldEditable} />
                                <FormField name="isUrgent" isEditable={erpFieldEditable} />
                            </FormRow>
                            <FormRow>
                                <FormField name="externalCode" isEditable={erpFieldEditable} />
                                <FormField name="erpNumber" isEditable={erpFieldEditable} />
                            </FormRow>
                            {isView && <FormField name="erpCreatedAt" />}
                            {isView && v.cancellationReason && <FormField name="cancellationReason" />}
                            <FormField name="comment" />
                        </FormSection>
                    </div>
                </div>
            </div>
        </section>
    )
})
